import { useState } from "react"
import { Link, router } from "@inertiajs/react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import type { TakeoffListRow } from "@/types/takeoff"

export function TakeoffsSection({
  projectId,
  takeoffs,
}: {
  projectId: number
  takeoffs: TakeoffListRow[]
}) {
  return (
    <section className="mt-10">
      <div className="flex items-center justify-between gap-4">
        <h2>Takeoffs</h2>
        <NewTakeoffDialog projectId={projectId} defaultName={`Takeoff ${takeoffs.length + 1}`} />
      </div>

      {takeoffs.length === 0 ? (
        <div className="callout mt-4">
          <p>
            No takeoffs yet. Open a promoted sheet in the editor (or create a
            takeoff here) to start measuring — work saves automatically.
          </p>
        </div>
      ) : (
        <ul className="mt-4 divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-page">
          {takeoffs.map((takeoff) => (
            <li key={takeoff.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <Link
                href={takeoff.url}
                className="font-medium text-ink-display no-underline hover:underline"
              >
                {takeoff.name}
              </Link>
              {takeoff.locked_by && <Badge tone="signal">{takeoff.locked_by} is editing</Badge>}
              <span className="text-xs text-ink-muted">
                created by {takeoff.created_by} · updated {formatDateTime(takeoff.updated_at)}
              </span>
              <span className="ml-auto flex gap-2">
                <Button asChild variant="ghost" size="sm">
                  <Link href={`${takeoff.url}/schedule`}>Material schedule</Link>
                </Button>
                <Button asChild variant="secondary" size="sm">
                  <Link href={takeoff.url}>Open</Link>
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function NewTakeoffDialog({ projectId, defaultName }: { projectId: number; defaultName: string }) {
  const [name, setName] = useState("")

  const create = () => {
    router.post(`/projects/${projectId}/takeoffs`, { name: name.trim() || defaultName })
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm">New takeoff</Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>New takeoff</DialogTitle>
          <DialogDescription>
            A takeoff is a measuring workspace — rooms traced across this
            project&apos;s sheets. Use alternates for options or revisions.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <label htmlFor="takeoff-name">Name</label>
          <Input
            id="takeoff-name"
            className="mt-1"
            placeholder={defaultName}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && create()}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">Cancel</Button>
          </DialogClose>
          <Button onClick={create}>Create takeoff</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
